import React, { useEffect, useRef, useState } from "react";

import { Box, Stack, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";

import {
  toEnd,
  toStart,
} from "@/components/molecules/insight/PenPortraitView/utils";
import { HelpBox } from "@/components/atoms";
import { InsightResult } from "@/types";
import { toBubbleColor, toIndex, toPercentage } from "@/utils";

interface DemographicCardProps {
  demographic: string;
  values: InsightResult[];
  score: string;
}

const DemographicCard: React.FC<DemographicCardProps> = ({
  demographic,
  values,
  score,
}) => {
  const theme = useTheme();

  const gridRef = useRef<HTMLDivElement>(null);
  const [gridWidth, setGridWidth] = useState<number>(150);

  // Use a ResizeObserver to keep track of the width of the grid item
  useEffect(() => {
    const node = gridRef.current;
    if (!node) return;

    const observer = new ResizeObserver(([entry]) => {
      setGridWidth(entry.contentRect.width);
    });

    observer.observe(node);

    return () => observer.disconnect();
  }, []);

  // Calculate the relative sizes of each bubble using the scores and total width
  const totalWidth = values
    .map((x) => x[score as keyof InsightResult] as number)
    .reduce((sum, v) => sum + v, 0);
  const availableWidth = Math.min(gridWidth, 300) - values.length * 4;
  const scale = availableWidth / totalWidth;
  const sizes = values.map((x) =>
    Math.max(
      10,
      Math.min((x[score as keyof InsightResult] as number) * scale, 45),
    ),
  );
  const actualWidth = sizes.reduce((sum, v) => sum + v, 0) + values.length * 4;

  return (
    <Box p={1} height={"116px"} ref={gridRef}>
      <Typography variant="body1">{demographic}</Typography>
      <Box
        display="flex"
        flexDirection="row"
        justifyContent="space-between"
        pt={1}
        width={actualWidth}
      >
        <Typography variant="caption">{toStart(demographic)}</Typography>
        <Typography variant="caption">{toEnd(demographic)}</Typography>
      </Box>
      <Stack
        direction="row"
        spacing={0.5}
        alignItems="center"
        sx={{ height: 45 }}
      >
        {values.map((row, idx) => (
          <HelpBox
            key={idx}
            title={`${row.tag_name} (${
              score === "rate"
                ? toPercentage(row[score as keyof InsightResult] as number)
                : toIndex(row[score as keyof InsightResult] as number)
            })`}
          >
            <Box>
              <Box
                sx={{
                  // height: toBubbleSize(row.score_wt),
                  // width: toBubbleSize(row.score_wt),
                  height: sizes[idx],
                  width: sizes[idx],
                  borderRadius: "50%",
                  backgroundColor: toBubbleColor(
                    row[score as keyof InsightResult] as number,
                    score,
                    theme.palette.gradient
                  ),
                }}
              ></Box>
            </Box>
          </HelpBox>
        ))}
      </Stack>
    </Box>
  );
};

export default DemographicCard;
